import React, { useEffect, useRef } from "react";
import type { OpenChatOptions } from "../providers/chat.js";

/**
 * Render arbitrary (already sanitized) HTML in a Shadow DOM so that
 * application-level CSS (e.g. Tailwind utility classes) does not leak in
 * and the embedded content's styles do not leak out.
 */
export interface IsolatedHtmlProps {
  /** Sanitized HTML string (consumer must remove scripts/links, etc.). */
  html: string;
  /** Optional external stylesheet URLs to inject inside the shadow root. */
  cssLinks?: string[];
  /** Inline CSS string injected via a <style> tag inside the shadow root. */
  inlineCss?: string;
  /** Class applied to the host div (outside the shadow boundary). */
  className?: string;
  /** If true, will clear and re-create shadow root on every html change (normally false). */
  resetOnChange?: boolean;
  /** Called when an AIER link (marked with data-magic-link) is clicked. The link is disabled until the promise resolves. */
  onMagicLinkClick?: (url: string) => Promise<void>;
  /** Called when an LQM link (marked with data-lqm-link) is clicked. The link is disabled until the promise resolves. */
  onLqmLinkClick?: () => Promise<void>;
  /**
   * Called when an <a href="#openChat"> link is clicked, instead of following the hash.
   * Optional `data-chat-prompt` and `data-chat-mode` ("chat" | "article") attributes are passed as options.
   */
  onOpenChatClick?: (options?: OpenChatOptions) => void;
}

export const IsolatedHtml: React.FC<IsolatedHtmlProps> = ({
  html,
  cssLinks = [],
  inlineCss = "",
  className,
  resetOnChange = false,
  onMagicLinkClick,
  onLqmLinkClick,
  onOpenChatClick,
}) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const shadowRef = useRef<ShadowRoot | null>(null);
  const onMagicLinkClickRef = useRef(onMagicLinkClick);
  const onLqmLinkClickRef = useRef(onLqmLinkClick);
  const onOpenChatClickRef = useRef(onOpenChatClick);

  useEffect(() => {
    onMagicLinkClickRef.current = onMagicLinkClick;
  }, [onMagicLinkClick]);

  useEffect(() => {
    onLqmLinkClickRef.current = onLqmLinkClick;
  }, [onLqmLinkClick]);

  useEffect(() => {
    onOpenChatClickRef.current = onOpenChatClick;
  }, [onOpenChatClick]);

  useEffect(() => {
    if (!hostRef.current) return;
    if (!shadowRef.current || resetOnChange) {
      // If resetting, detach previous contents by creating a fresh shadow root (cannot "replace" one; so only do if requested)
      if (resetOnChange && hostRef.current.shadowRoot) {
        // Reuse existing shadow root
        shadowRef.current = hostRef.current.shadowRoot;
      } else if (!shadowRef.current) {
        shadowRef.current = hostRef.current.attachShadow({ mode: "open" });
      }
    }

    if (!shadowRef.current) return;

    // Clear previous children so we can re-populate.
    shadowRef.current.innerHTML = "";

    // We hide content until every external stylesheet has either loaded or errored
    // to avoid a flash of unstyled content.
    const pendingLinks: HTMLLinkElement[] = [];

    cssLinks.forEach(href => {
      if (!href || !shadowRef.current) return;
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      pendingLinks.push(link);
      shadowRef.current.appendChild(link);
    });

    if (inlineCss) {
      const styleTag = document.createElement("style");
      styleTag.textContent = inlineCss;
      shadowRef.current.appendChild(styleTag);
    }

    const wrapper = document.createElement("div");
    wrapper.className = "_isolated-root"; // internal root marker
    wrapper.innerHTML = html; // html is assumed sanitized

    // Hide until styles ready (only if we actually have external links)
    const shouldDeferVisibility = pendingLinks.length > 0;
    if (shouldDeferVisibility) {
      wrapper.style.visibility = "hidden";
      wrapper.style.opacity = "0";
      wrapper.style.transition = "opacity 200ms ease";
    }

    shadowRef.current.appendChild(wrapper);

    if (shouldDeferVisibility) {
      let remaining = pendingLinks.length;
      const markReady = () => {
        remaining -= 1;
        if (remaining <= 0) {
          wrapper.style.visibility = "visible";
          // Trigger fade-in
          requestAnimationFrame(() => {
            wrapper.style.opacity = "1";
          });
        }
      };
      pendingLinks.forEach(link => {
        link.addEventListener("load", markReady, { once: true });
        link.addEventListener("error", markReady, { once: true });
      });
      // Safety timeout: if something hangs, reveal after 4s so content isn't permanently hidden
      setTimeout(() => {
        if (wrapper.style.visibility === "hidden") {
          wrapper.style.visibility = "visible";
          wrapper.style.opacity = "1";
        }
      }, 4000);
    }

    const shadowRoot = shadowRef.current;
    if (!shadowRoot) return;

    const handleClick = async (event: Event) => {
      const target = event.target as Element;
      const openChatAnchor = target.closest("a") as HTMLAnchorElement | null;
      if (openChatAnchor?.hash === "#openChat" && onOpenChatClickRef.current) {
        event.preventDefault();
        const prompt = openChatAnchor.dataset.chatPrompt?.trim() || undefined;
        const rawMode = openChatAnchor.dataset.chatMode;
        const mode = rawMode === "chat" ? "chat" : rawMode === "article" ? "chat_article" : undefined;
        onOpenChatClickRef.current(prompt || mode ? { prompt, mode } : undefined);
        return;
      }

      const anchor = target.closest("a[data-magic-link]") as HTMLAnchorElement | null;
      if (anchor && onMagicLinkClickRef.current) {
        event.preventDefault();
        anchor.style.pointerEvents = "none";
        try {
          await onMagicLinkClickRef.current(anchor.href);
        } finally {
          anchor.style.pointerEvents = "";
        }
        return;
      }

      const lqmAnchor = target.closest("a[data-lqm-link]") as HTMLAnchorElement | null;
      if (lqmAnchor && onLqmLinkClickRef.current) {
        event.preventDefault();
        lqmAnchor.style.pointerEvents = "none";
        try {
          await onLqmLinkClickRef.current();
        } finally {
          lqmAnchor.style.pointerEvents = "";
        }
      }
    };

    shadowRoot.addEventListener("click", handleClick);
    return () => {
      shadowRoot.removeEventListener("click", handleClick);
    };
  }, [html, cssLinks, inlineCss, resetOnChange]);

  return <div ref={hostRef} className={className} />;
};

export default IsolatedHtml;
